import { Dropdown, Table, Form, Button, InputGroup } from "react-bootstrap";
import { CSVLink } from "react-csv";
import {
  ExportIcon,
  FilterTwoIcon,
  SummaryChartIconUp,
  SummaryCloseIcon,
  FilterCollapseIcon,
  ExcelIcon,
  PDFIcon,
  MoneyDollarIcon,
} from "./../Icons";
import "../../assets/scss/reports/cashbook.scss";
import { useEffect, useRef, useState } from "react";
import CachedIcon from "mdi-react/CachedIcon";
import Select from "react-select";
import useDebounce, {
  useLocalStorage,
  useQueryParams,
  useScrollTop,
} from "../../utils/hooks";
import { useQuery } from "react-query";
import { endOfMonth, format, parse, startOfMonth } from "date-fns";
import ReactPaginate from "react-paginate";
import {
  defaultSelectValue,
  formatDate,
  paginationOptions,
  reactSelectTheme,
} from "../../utils/helpers";
import RsDateRangePicker from "../utils/RsDateRangePicker";
import queryString from "query-string";
import { isEmpty } from "lodash";
import NoTableItem from "../../components/utils/NoTableItem";
import ModalLoader from "../../components/utils/ModalLoader";
import VendorSelector from "./../Vendor/VendorSelector";
import { Popover } from "react-tiny-popover";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import { Link } from "react-router-dom";
import MonthYearPicker from "./../utils/MonthYearPicker";
import { useAuth } from "../../hooks/useAuth";
import { useDownloadExcel } from "../../hooks/useDownloadExcel";

const statusOptions = [
  { label: "All", value: "" },
  { label: "Not Loaded", value: "Not Loaded" },
  { label: "Partially Loaded", value: "Partially Loaded" },
  { label: "Loaded", value: "Loaded" },
];

const formatMoney = value => {
  if (value === null || value === undefined || value === "") return "—";

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return value;

  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 2,
  }).format(numericValue);
};

export default function IncentiveLedger() {
  useScrollTop();

  const initialFilterParams = {
    // q: "",
    // Status: "",
    // enableDateRange: true,
    // Vendor_ID: "",

    startDate: format(startOfMonth(new Date()), "yyyy-MM-dd"),
    endDate: format(endOfMonth(new Date()), "yyyy-MM-dd"),
    month: format(new Date(), "M"), // current month 1,2,3,4
    year: format(new Date(), "yyyy"), // current year 2026, 2027
  };

  const { backendUrl } = useAuth();

  const [showFilter, setShowFilter] = useLocalStorage("showFilter", true);
  const [btnSummryActive, setBtnSummaryActive] = useState(false);
  const [excelData, setExcelData] = useState([]);
  const [showVendorPopover, setShowVendorPopover] = useState(false);
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [selectedIncentive, setSelectedIncentive] = useState(null);
  const [settleAll, setSettleAll] = useState(false);

  const CSVLinkRef = useRef(null);

  const [isfetchingExcel, fetchExcelData] = useDownloadExcel(
    excelData,
    CSVLinkRef
  );

  const close = () => {
    setBtnSummaryActive(false);
  };

  const [queryParams, setQueryParams] = useQueryParams({
    page: 1,
    limit: 40,
    ...initialFilterParams,
  });

  const [filterParams, setFilterParams] = useState({
    ...initialFilterParams,
    ...queryParams,
  });

  const debouncedFilterParams = useDebounce(filterParams, 500);
  useEffect(() => {
    setQueryParams({
      ...queryParams,
      ...debouncedFilterParams,
    });
  }, [debouncedFilterParams]);

  const fetchStocking = async queryParams => {
    let response = await fetch(
      `${backendUrl}/api/incentive/ledger?${queryString.stringify(
        queryParams
      )}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();
    return data;
  };

  const {
    data = {
      count: 0,
      incentives: [],
    },
    refetch,
    isSuccess,
    isFetching,
  } = useQuery(
    ["DUE_INCENTIVES", queryParams],
    () => fetchStocking(queryParams),
    {
      keepPreviousData: true,
    }
  );

  const { limit, page, ...rest } = queryParams;
  const onDownloadExcelData = async () => {
    let exData = await fetchExcelData(
      `${backendUrl}/api/incentives/due?${queryString.stringify(rest)}`,
      "GET"
    );

    const company = exData.data?.company?.CompName;

    exData = (
      exData.data?.dueIncentives ||
      exData.data?.vendorLedgers ||
      []
    ).map(d => [
      d.Trans_ID || "",
      formatMoney(d.AmountDue),
      formatMoney(d.AmountPaid),
      formatMoney(d.Balance),
    ]);

    const date =
      rest.startDate && rest.endDate
        ? `Date Prepared: Between ${format(
            new Date(rest.startDate),
            "E MMM d yyyy"
          )} to ${format(new Date(rest.endDate), "E MMM d yyyy")}`
        : "";

    exData = [
      [company],
      ["Due Incentives Report"],
      [date],
      [""],
      ["Transaction ID", "Amount Due", "Amount Paid", "Balance"],
      ...exData,
    ];

    setExcelData(exData);
  };

  const handleFilterParamsChange = e => {
    setFilterParams({
      ...filterParams,
      [e.target.name]:
        e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });
  };

  const handleSearchQueryChange = e => {
    setQueryParams({
      ...queryParams,
      [e.target.name]: e.target.value,
    });
  };

  const filterByDateRange = date => {
    setFilterParams({
      ...filterParams,
      startDate: format(date[0], "yyyy-MM-dd"),
      endDate: format(date[1], "yyyy-MM-dd"),
      enableDateRange: true,
    });
  };

  const clearDateRange = () => {
    setFilterParams({
      ...filterParams,
      startDate: "",
      endDate: "",
    });
  };

  const search = () => {
    setQueryParams({
      ...queryParams,
      ...filterParams,
    });
  };

  const reset = () => {
    setFilterParams(initialFilterParams);
    setQueryParams({
      ...queryParams,
      ...initialFilterParams,
    });
  };

  const handleSelectedVendor = vendor => {
    setFilterParams({
      ...filterParams,
      Vendor_ID: vendor.Vendor_ID,
    });
    setShowVendorPopover(false);
  };

  return (
    <main className="cash-book">
      <div className="content">
        <div className="content-main">
          <div className={`filter-area ${showFilter ? "open" : "close"}`}>
            <div className="filter-header">
              <h2>
                <FilterTwoIcon /> Filters
              </h2>
              <Button
                variant="white"
                onClick={() => setShowFilter(!showFilter)}
              >
                <FilterCollapseIcon />
              </Button>
            </div>
            <div className="filter-body">
              <Form>
                <div className="body">
                  <Form.Group className="mb-2-5">
                    <Form.Label>Transaction ID</Form.Label>
                    <Form.Control
                      name="q"
                      placeholder="Search transaction ID..."
                      value={filterParams.q}
                      onChange={e => handleFilterParamsChange(e)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2-5">
                    <Form.Label>Vendor ID</Form.Label>
                    <InputGroup>
                      <Form.Control
                        name="Vendor_ID"
                        placeholder="Enter Vendor ID"
                        value={filterParams?.Vendor_ID}
                        onChange={() => {}}
                        readOnly
                      />
                      <Popover
                        isOpen={showVendorPopover}
                        onClickOutside={() => setShowVendorPopover(false)}
                        content={() => (
                          <VendorSelector
                            handleSelectedCustomer={handleSelectedVendor}
                            setShowCustomerSelector={setShowVendorPopover}
                          />
                        )}
                        position="bottom"
                      >
                        <InputGroup.Text
                          onClick={() =>
                            setShowVendorPopover(!showVendorPopover)
                          }
                        >
                          <MagnifyIcon />
                        </InputGroup.Text>
                      </Popover>
                    </InputGroup>
                  </Form.Group>

                  {/* <Form.Group className="mb-2-5">
                    <Form.Label>Status</Form.Label>
                    <Select
                      classNamePrefix="form-select"
                      placeholder="All"
                      name="Status"
                      theme={reactSelectTheme}
                      isSearchable={false}
                      onChange={selected => {
                        setFilterParams({
                          ...filterParams,
                          Status: selected.value,
                        });
                      }}
                      defaultValue={defaultSelectValue(
                        queryParams?.Status,
                        statusOptions,
                        { value: "", label: "All" }
                      )}
                      options={statusOptions}
                    />
                  </Form.Group> */}

                  <hr className="mt-1 mb-4" />

                  <Form.Group className="">
                    <Form.Label>Date Range</Form.Label>
                    <div className="position-relative">
                      <RsDateRangePicker
                        placement="auto"
                        value={
                          filterParams.startDate && filterParams.endDate
                            ? [
                                parse(
                                  filterParams.startDate,
                                  "yyyy-MM-dd",
                                  new Date()
                                ),
                                parse(
                                  filterParams.endDate,
                                  "yyyy-MM-dd",
                                  new Date()
                                ),
                              ]
                            : []
                        }
                        onClean={() => clearDateRange()}
                        onOk={date => filterByDateRange(date)}
                      />
                    </div>
                  </Form.Group>
                </div>

                <div className="buttons rounded">
                  <Button onClick={() => reset()} variant="white">
                    Reset
                  </Button>
                  <Button onClick={() => search()} variant="primary">
                    Search
                  </Button>
                </div>
              </Form>
            </div>
          </div>

          <div className="content-body">
            <header>
              <h1>
                {!showFilter && (
                  <button
                    onClick={() => setShowFilter(!showFilter)}
                    className="btn filter"
                  >
                    <FilterTwoIcon />
                  </button>
                )}
                Incentive Ledger
                <button onClick={() => refetch()} className="btn text-primary">
                  <CachedIcon />
                </button>
              </h1>

              {/* <div style={{ zIndex: 10 }}>
                <MonthYearPicker
                  onMonthChange={month => {
                    const monthIndex = Number(month) - 1;

                    setFilterParams({
                      ...filterParams,
                      month: month || "",
                      startDate: format(
                        startOfMonth(new Date(filterParams.year, monthIndex)),
                        "yyyy-MM-dd"
                      ),
                      endDate: format(
                        endOfMonth(new Date(filterParams.year, monthIndex)),
                        "yyyy-MM-dd"
                      ),
                    });
                  }}
                  onYearChange={year => {
                    const monthIndex = Number(filterParams.month) - 1;

                    setFilterParams({
                      ...filterParams,
                      year: year || "",
                      startDate: format(
                        startOfMonth(new Date(year, monthIndex)),
                        "yyyy-MM-dd"
                      ),
                      endDate: format(
                        endOfMonth(new Date(year, monthIndex)),
                        "yyyy-MM-dd"
                      ),
                    });
                  }}
                  yearValue={filterParams?.year}
                  monthValue={Number(filterParams?.month)}
                  variant="filter"
                  monthLabel=""
                  yearLabel=""
                />
              </div> */}
            </header>

            <div className="px-md-4">
              <Table responsive borderless striped className="product-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Incentive</th>
                    <th>Credit</th>
                    <th>Debit</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.incentives).map((el, index) => (
                    <tr key={index}>
                      <td>{el.Trans_ID || "—"}</td>
                      <td>{el.incentiveRef?.Name || "—"}</td>
                      <td>{formatMoney(el?.Credit || 0)}</td>
                      <td>{formatMoney(el?.Debit || 0)}</td>
                      <td>{formatDate(el?.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {!isFetching && isSuccess && isEmpty(data?.incentives) ? (
                <NoTableItem queryParams={queryParams} />
              ) : null}
            </div>

            <div className="d-flex justify-content-between px-3 align-items-center pagination">
              <div className="pagination_left">
                <p className="m-0 p-0">Show</p>
                <select
                  value={queryParams.limit}
                  name="limit"
                  className="form-select"
                  onChange={e => handleSearchQueryChange(e)}
                >
                  <option value="10">10 rows</option>
                  <option value="20">20 rows</option>
                  <option value="30">30 rows</option>
                  <option value="40">40 rows</option>
                  <option value="50">50 rows</option>
                  <option value="100">100 rows</option>
                </select>
              </div>

              <ReactPaginate
                {...paginationOptions}
                pageCount={Math.ceil(data.count / queryParams.limit)}
                marginPagesDisplayed={2}
                pageRangeDisplayed={0}
                onPageChange={({ selected }) => {
                  document.body.scrollTop = document.documentElement.scrollTop = 0;
                  setQueryParams({
                    ...queryParams,
                    page: selected + 1,
                  });
                }}
                forcePage={queryParams.page - 1}
              />
            </div>
          </div>
        </div>
      </div>
      <ModalLoader show={isfetchingExcel} />
    </main>
  );
}
